import api from './api';
import {
  UsersListResponse,
  UserResponse,
  StatsResponse,
  DeleteUserResponse,
  CreateUserData,
  UpdateUserData,
  PromptsListResponse,
  PromptResponse,
  UpdatePromptData,
  PromptType,
} from '@/types';

export const adminApi = {
  /**
   * Get admin statistics
   */
  getStats: async (): Promise<StatsResponse> => {
    const { data } = await api.get<StatsResponse>('/admin/stats');
    return data;
  },

  /**
   * Get all users with optional filters
   */
  getUsers: async (params?: {
    search?: string;
    role?: 'USER' | 'ADMIN';
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<UsersListResponse> => {
    const { data } = await api.get<UsersListResponse>('/admin/users', {
      params,
    });
    return data;
  },

  /**
   * Get user by ID
   */
  getUserById: async (id: number): Promise<UserResponse> => {
    const { data } = await api.get<UserResponse>(`/admin/users/${id}`);
    return data;
  },

  /**
   * Create new user
   */
  createUser: async (userData: CreateUserData): Promise<UserResponse> => {
    const { data } = await api.post<UserResponse>('/admin/users', userData);
    return data;
  },

  /**
   * Update user
   */
  updateUser: async (
    id: number,
    userData: UpdateUserData
  ): Promise<UserResponse> => {
    const { data } = await api.put<UserResponse>(
      `/admin/users/${id}`,
      userData
    );
    return data;
  },

  /**
   * Delete user
   */
  deleteUser: async (id: number): Promise<DeleteUserResponse> => {
    const { data } = await api.delete<DeleteUserResponse>(`/admin/users/${id}`);
    return data;
  },

  /**
   * Verify super admin access with password
   */
  verifySuperAdminAccess: async (password: string): Promise<{
    success: boolean;
    message: string;
    token: string;
    expiresIn: number;
  }> => {
    const { data } = await api.post('/admin/prompts/verify-access', { password });
    return data;
  },

  /**
   * Get all AI prompts (requires super admin token)
   */
  getPrompts: async (superAdminToken: string): Promise<PromptsListResponse> => {
    const { data } = await api.get<PromptsListResponse>('/admin/prompts', {
      headers: {
        Authorization: `Bearer ${superAdminToken}`,
      },
    });
    return data;
  },

  /**
   * Get prompt by type (requires super admin token)
   */
  getPromptByType: async (type: PromptType, superAdminToken: string): Promise<PromptResponse> => {
    const { data } = await api.get<PromptResponse>(`/admin/prompts/${type}`, {
      headers: {
        Authorization: `Bearer ${superAdminToken}`,
      },
    });
    return data;
  },

  /**
   * Update prompt (requires super admin token)
   */
  updatePrompt: async (
    type: PromptType,
    promptData: UpdatePromptData,
    superAdminToken: string
  ): Promise<PromptResponse> => {
    const { data} = await api.put<PromptResponse>(
      `/admin/prompts/${type}`,
      promptData,
      {
        headers: {
          Authorization: `Bearer ${superAdminToken}`,
        },
      }
    );
    return data;
  },

  /**
   * Get branding configuration (requires super admin token)
   */
  getBranding: async (superAdminToken: string): Promise<{
    success: boolean;
    branding: {
      id: number;
      appName: string;
      logoUrl: string | null;
      createdAt: string;
      updatedAt: string;
    };
  }> => {
    const { data } = await api.get('/admin/branding', {
      headers: {
        Authorization: `Bearer ${superAdminToken}`,
      },
    });
    return data;
  },

  /**
   * Update branding configuration (requires super admin token)
   */
  updateBranding: async (
    superAdminToken: string,
    brandingData: {
      appName: string;
      logoUrl: string | null;
    }
  ): Promise<{
    success: boolean;
    branding: {
      id: number;
      appName: string;
      logoUrl: string | null;
      createdAt: string;
      updatedAt: string;
    };
  }> => {
    const { data } = await api.put(
      '/admin/branding',
      brandingData,
      {
        headers: {
          Authorization: `Bearer ${superAdminToken}`,
        },
      }
    );
    return data;
  },
};
